import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  onSnapshot,
  writeBatch,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';

// Collection references
const notificationsCollection = collection(db, 'notifications');

/**
 * Create a new notification
 */
export async function createNotification(notificationData) {
  try {
    const notification = {
      ...notificationData,
      created_at: serverTimestamp(),
      read_at: null,
      status: 'UNREAD'
    };
    
    const docRef = await addDoc(notificationsCollection, notification);
    return { id: docRef.id, ...notification };
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
}

/**
 * Get notifications for a seller with filters
 */
export async function getNotifications(sellerId, filters = {}) {
  try {
    if (!sellerId) {
      console.warn('No sellerId provided for getNotifications');
      return [];
    }

    const {
      status = 'ALL',
      type = 'ALL',
      since = null,
      limit: limitCount = 50,
      cursor = null
    } = filters;

    let q = query(notificationsCollection, where('seller_id', '==', sellerId));

    // Apply status filter
    if (status !== 'ALL') {
      q = query(q, where('status', '==', status));
    }

    // Apply type filter
    if (type !== 'ALL') {
      q = query(q, where('type', '==', type));
    }

    // Apply time filter
    if (since) {
      const sinceDate = new Date(since);
      q = query(q, where('created_at', '>=', Timestamp.fromDate(sinceDate)));
    }

    // Order by created_at descending
    q = query(q, orderBy('created_at', 'desc'));

    // Apply limit
    if (limitCount) {
      q = query(q, limit(limitCount));
    }

    const snapshot = await getDocs(q);
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return notifications;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    // Return empty array instead of throwing to prevent app crashes
    return [];
  }
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(notificationId) {
  try {
    const notificationRef = doc(notificationsCollection, notificationId);
    await updateDoc(notificationRef, {
      status: 'READ',
      read_at: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
}

/**
 * Mark all notifications as read for a seller
 */
export async function markAllNotificationsAsRead(sellerId) {
  try {
    const q = query(
      notificationsCollection,
      where('seller_id', '==', sellerId),
      where('status', '==', 'UNREAD')
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch();
    
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: 'READ',
        read_at: serverTimestamp()
      });
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
}

/**
 * Get unread count for a seller
 */
export async function getUnreadCount(sellerId) {
  try {
    if (!sellerId) {
      console.warn('No sellerId provided for getUnreadCount');
      return 0;
    }

    const q = query(
      notificationsCollection,
      where('seller_id', '==', sellerId),
      where('status', '==', 'UNREAD')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error getting unread count:', error);
    // Return 0 instead of throwing to prevent app crashes
    return 0;
  }
}

/**
 * Delete old notifications (older than 30 days)
 */
export async function cleanupOldNotifications() {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const q = query(
      notificationsCollection,
      where('created_at', '<', Timestamp.fromDate(thirtyDaysAgo))
    );
    
    const snapshot = await getDocs(q);
    const batch = writeBatch();
    
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error cleaning up old notifications:', error);
    throw error;
  }
}

/**
 * Subscribe to real-time notification updates
 * @deprecated Temporarily disabled due to Firestore internal assertion errors
 */
export function subscribeToNotifications(sellerId, callback) {
  console.warn('Real-time notifications temporarily disabled due to Firestore errors');
  return () => {};
}

/**
 * Subscribe to unread count updates
 * @deprecated Temporarily disabled due to Firestore internal assertion errors
 */
export function subscribeToUnreadCount(sellerId, callback) {
  console.warn('Real-time unread count temporarily disabled due to Firestore errors');
  return () => {};
}

/**
 * Test function to create a notification with enhanced message format
 * Call this from browser console: window.testNotification()
 */
export async function testNotification() {
  const testOrderData = {
    id: 'test-order-' + Date.now(),
    sellerId: 'sf76MdxXTdeRWhVKvvFCmATJRu33',
    storeId: 'test-store',
    orderNumber: 'TEST' + Math.floor(Math.random() * 1000),
    customerName: 'Test Customer',
    serviceType: 'pickup',
    serviceDate: '2024-09-21T17:00:00Z', // Friday Sept 21st
    windowStart: '5:00 PM',
    windowEnd: '7:00 PM',
    totalAmount: 1200, // $12.00 in cents
    itemCount: 1
  };

  console.log('Creating test notification with enhanced message...');
  console.log('Test data:', testOrderData);

  try {
    const result = await createNewOrderNotification(testOrderData);
    console.log('✅ Notification created successfully:', result);
    return result;
  } catch (error) {
    console.error('❌ Error creating notification:', error);
    throw error;
  }
}

/**
 * Create notification for new order
 */
export async function createNewOrderNotification(orderData) {
  // Format the fulfillment date with ordinal suffix
  const formatFulfillmentDate = (dateString) => {
    if (!dateString) return 'Today';
    
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
      
      // Add ordinal suffix (1st, 2nd, 3rd, etc.)
      const day = date.getDate();
      const ordinal = day === 1 || day === 21 || day === 31 ? 'st' :
                     day === 2 || day === 22 ? 'nd' :
                     day === 3 || day === 23 ? 'rd' : 'th';
      
      return dateStr.replace(/\d+/, `${day}${ordinal}`);
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Format amount from cents to dollars
  const formatAmount = (amount) => {
    const dollars = (amount / 100).toFixed(2);
    return dollars;
  };

  // Get item count (default to 1 if not provided)
  const itemCount = orderData.itemCount || 1;
  const itemText = itemCount === 1 ? 'item' : 'items';

  // Format fulfillment date
  const fulfillmentDate = formatFulfillmentDate(orderData.serviceDate);
  
  // Create enhanced title and body
  const title = `You have a new $${formatAmount(orderData.totalAmount)} pickup order for ${fulfillmentDate} containing ${itemCount} ${itemText}`;
  
  const body = `Order #${orderData.orderNumber} • Pickup: ${fulfillmentDate} • ${orderData.windowStart || 'ASAP'} • Placed by: ${orderData.customerName || 'Customer'} • ${itemCount} ${itemText}`;

  const notification = {
    seller_id: orderData.sellerId,
    store_id: orderData.storeId,
    type: 'NEW_ORDER',
    title: title,
    body: body,
    context: {
      order_id: orderData.id,
      order_number: orderData.orderNumber,
      service_date: orderData.serviceDate,
      window_start_local: orderData.windowStart,
      window_end_local: orderData.windowEnd,
      amount_total: orderData.totalAmount,
      currency: 'USD'
    },
    primary_action: {
      label: 'View Order',
      href: `/order-details/${orderData.id}`
    },
    dedupe_key: `NEW_ORDER:${orderData.id}`
  };
  
  return createNotification(notification);
}

/**
 * Create notification for due soon order
 */
export async function createDueSoonNotification(orderData) {
  const notification = {
    seller_id: orderData.sellerId,
    store_id: orderData.storeId,
    type: 'DUE_SOON',
    title: `Order #${orderData.orderNumber} due soon`,
    body: `Order #${orderData.orderNumber} due in ${orderData.timeUntilDue} • ${orderData.productName} • ${orderData.windowStart}–${orderData.windowEnd}`,
    context: {
      order_id: orderData.id,
      order_number: orderData.orderNumber,
      service_date: orderData.serviceDate,
      window_start_local: orderData.windowStart,
      window_end_local: orderData.windowEnd,
      product_name: orderData.productName
    },
    primary_action: {
      label: 'Mark as ready',
      href: `/orders/${orderData.orderNumber}`
    },
    dedupe_key: `DUE_SOON:${orderData.id}:${orderData.serviceDate}`
  };
  
  return createNotification(notification);
}

/**
 * Create notification for overdue order
 */
export async function createOverdueNotification(orderData) {
  const notification = {
    seller_id: orderData.sellerId,
    store_id: orderData.storeId,
    type: 'OVERDUE',
    title: `Overdue: Order #${orderData.orderNumber}`,
    body: `Overdue: Order #${orderData.orderNumber} • ${orderData.serviceType} window started ${orderData.timeOverdue} ago`,
    context: {
      order_id: orderData.id,
      order_number: orderData.orderNumber,
      service_date: orderData.serviceDate,
      window_start_local: orderData.windowStart,
      window_end_local: orderData.windowEnd
    },
    primary_action: {
      label: 'View order',
      href: `/orders/${orderData.orderNumber}`
    },
    dedupe_key: `OVERDUE:${orderData.id}:${orderData.serviceDate}`
  };
  
  return createNotification(notification);
}

/**
 * Create notification for low inventory
 */
export async function createLowInventoryNotification(productData, serviceDate, remainingTotal) {
  const notification = {
    seller_id: productData.sellerId,
    store_id: productData.storeId,
    type: 'LOW_INVENTORY',
    title: `Low inventory: ${productData.name}`,
    body: `Low inventory: ${productData.name} on ${serviceDate} • Remaining: ${remainingTotal}`,
    context: {
      product_id: productData.id,
      product_name: productData.name,
      service_date: serviceDate,
      remaining_total: remainingTotal
    },
    primary_action: {
      label: 'Edit inventory',
      href: `/all-products?product=${productData.id}&date=${serviceDate}`
    },
    dedupe_key: `LOW_INV:${productData.id}:${serviceDate}`
  };
  
  return createNotification(notification);
}

/**
 * Send notification to customer about order status change
 * @deprecated Use createNewOrderNotification for seller notifications instead
 */
export async function sendOrderStatusNotification(orderData, newStatus) {
  try {
    if (!orderData.userId) {
      console.warn('No userId found for order, skipping notification');
      return;
    }

    const statusMessages = {
      'preparing': 'Your order is now being prepared!',
      'ready': 'Your order is ready for pickup!',
      'delivered': 'Your order has been delivered!',
      'completed': 'Your order has been completed!',
      'cancelled': 'Your order has been cancelled.'
    };

    const message = statusMessages[newStatus] || `Your order status has been updated to: ${newStatus}`;

    const notification = {
      userId: orderData.userId,
      orderId: orderData.id,
      type: 'order_status_update',
      title: 'Order Update',
      message: message,
      status: newStatus,
      read: false,
      createdAt: serverTimestamp(),
      data: {
        orderId: orderData.id,
        status: newStatus,
        orderTotal: orderData.total
      }
    };

    await addDoc(notificationsCollection, notification);
    
    console.log('Notification sent for order status update:', orderData.id, newStatus);
  } catch (error) {
    console.error('Error sending order status notification:', error);
    // Don't throw error to avoid breaking the order update flow
  }
}

/**
 * Send notification for order cancellation
 * @deprecated Use createNewOrderNotification for seller notifications instead
 */
export async function sendOrderCancellationNotification(orderData, reason = '') {
  try {
    if (!orderData.userId) {
      console.warn('No userId found for order, skipping cancellation notification');
      return;
    }

    const notification = {
      userId: orderData.userId,
      orderId: orderData.id,
      type: 'order_cancelled',
      title: 'Order Cancelled',
      message: `Your order has been cancelled.${reason ? ` Reason: ${reason}` : ''}`,
      status: 'cancelled',
      read: false,
      createdAt: serverTimestamp(),
      data: {
        orderId: orderData.id,
        reason: reason,
        refundAmount: orderData.total
      }
    };

    await addDoc(notificationsCollection, notification);
    
    console.log('Cancellation notification sent for order:', orderData.id);
  } catch (error) {
    console.error('Error sending cancellation notification:', error);
  }
}

/**
 * Send notification for item cancellation
 * @deprecated Use createNewOrderNotification for seller notifications instead
 */
export async function sendItemCancellationNotification(orderData, itemData, reason = '') {
  try {
    if (!orderData.userId) {
      console.warn('No userId found for order, skipping item cancellation notification');
      return;
    }

    const notification = {
      userId: orderData.userId,
      orderId: orderData.id,
      type: 'item_cancelled',
      title: 'Item Cancelled',
      message: `${itemData.name} has been removed from your order.${reason ? ` Reason: ${reason}` : ''}`,
      status: 'item_cancelled',
      read: false,
      createdAt: serverTimestamp(),
      data: {
        orderId: orderData.id,
        itemId: itemData.id,
        itemName: itemData.name,
        reason: reason,
        refundAmount: itemData.price * itemData.quantity
      }
    };

    await addDoc(notificationsCollection, notification);
    
    console.log('Item cancellation notification sent for order:', orderData.id, 'item:', itemData.id);
  } catch (error) {
    console.error('Error sending item cancellation notification:', error);
  }
}